import { Table, TableWrapper, Tbody, Td, Th, Thead, Tr } from '@components/core-ui/Table';
import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { Input } from '@components/core-ui/form-elements/Input';
import { IconButtonDelete } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonDelete';
import Search from '@components/icons/Search.svg';
import TrashBin from '@components/icons/TrashBin.svg';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { useAuthContext } from '@providers/AuthProvider';
import { useAPI } from '@providers/useAPI';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { GetOrgMembersMemberResponseDto, PrismaUserDto, RepositoriesService } from 'app/API';
import { useDebouncedValue } from 'app/hooks/useDebouncedValue';
import { useNotifyQueryError } from 'app/hooks/useNotifyQueryError';
import { notifyApiCallError } from 'app/http/notifyApiCallError';
import { createFetchAssignableMembersQueryFn } from 'app/queries/organizations/createFetchAssignableMembersQueryFn';
import { organizationsKeys } from 'app/queries/organizations/organizationsKeys';
import { TRepository } from 'app/types/entities/TRepository';
import React from 'react';
import { Content } from './AssignRepositoryModal/styles/Content';

const SEARCH_DEBOUNCE_MS = 500;
const MEMBERS_PAGE_SIZE = 1000;

type AssigneeLike = GetOrgMembersMemberResponseDto | PrismaUserDto;

type TProps = {
  repositoryId: string;
  repository: TRepository & { assignee?: AssigneeLike | null };
  onCancel: () => void;
  onSuccess: (assignee: AssigneeLike | null) => void;
};

export const AssignRepositoryModal = ({ repositoryId, repository, onCancel, onSuccess }: TProps) => {
  const { call } = useAPI();
  const { organization } = useAuthContext();
  const [search, setSearch] = React.useState('');
  const [assignee, setAssignee] = React.useState<AssigneeLike | null>(repository.assignee ?? null);

  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const organizationId = organization?.id ?? '';

  const membersQuery = useQuery({
    queryKey: organizationsKeys.assignableMembers(organizationId, {
      pageSize: MEMBERS_PAGE_SIZE,
      orderBy: 'name',
      orderType: 'asc',
      search: debouncedSearch,
      teamsSlugFilter: '',
    }),
    queryFn: createFetchAssignableMembersQueryFn(call),
    enabled: organizationId !== '' && search === debouncedSearch,
    placeholderData: keepPreviousData,
  });
  useNotifyQueryError(membersQuery.error);
  const users: GetOrgMembersMemberResponseDto[] = membersQuery.data?.data ?? [];

  const changeAssignee = (data: AssigneeLike | null) => {
    if (!data) {
      setAssignee(null);
      return;
    }
    setAssignee(data);
  };

  const renderAssignee = () => {
    if (!assignee) {
      return;
    }
    return (
      <div className="assignTo">
        <div className="assignToText">
          <p>
            {assignee.name || ''} {assignee.lastname || ''}
          </p>
          {assignee.email ? <p>{assignee.email}</p> : ''}
        </div>
        <IconButtonDelete type="button" title="Clear assignee" onClick={() => changeAssignee(null)}>
          <TrashBin />
        </IconButtonDelete>
      </div>
    );
  };

  const reassignMutation = useMutation({
    mutationFn: (nextAssignee: AssigneeLike | null) =>
      call(() =>
        RepositoriesService.reassignRepo({ repositoryId, requestBody: { assigneeId: nextAssignee?.id ?? null } }),
      ),
    onSuccess: (_, nextAssignee) => onSuccess(nextAssignee),
    onError: notifyApiCallError,
  });

  return (
    <ModalWrapper width="487px">
      <ModalTitle>Edit assignee</ModalTitle>
      <Content>
        <p className="assignedTo">{repository.assigneeId ? 'Currently assigned to' : 'Currently not assigned'}</p>
        {renderAssignee()}
        <Input
          onChange={(e) => setSearch(e.target.value)}
          label="Search user to assign"
          name="assignUser"
          icon={Search}
          value={search}
        />
        <TableWrapper className="customTable">
          <Table>
            <Thead $white>
              <Tr>
                <Th>Name</Th>
                <Th>Email</Th>
              </Tr>
            </Thead>
            <Tbody>
              {users.map((data) => (
                <Tr key={data.id}>
                  <Td>
                    <p className="cursor-pointer" onClick={() => changeAssignee(data)}>
                      {data?.name}
                    </p>
                  </Td>
                  <Td>
                    <p className="cursor-pointer" onClick={() => changeAssignee(data)}>
                      {data?.email}
                    </p>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableWrapper>

        <div className="buttonGroup">
          <OutlinedButton onClick={onCancel}>Cancel</OutlinedButton>
          <Button onClick={() => reassignMutation.mutate(assignee)} disabled={reassignMutation.isPending}>
            Save
          </Button>
        </div>
      </Content>
    </ModalWrapper>
  );
};
